import * as React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import useResizeObserver from 'hooks/useResizeObserver';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { color, variants } from 'styles/utils';

const Container = styled.div`
  background-color: ${color('darkestTeal')};
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;

  & > * {
    position: absolute;
    inset: 0;
  }
`;

const Clouds = styled.div`
  --color-0: #051620;
  --color-1: #0f2f40;
  --color-2: #5195b9;

  background: var(--color-0) no-repeat;
  filter: blur(1rem);

  @supports (color: oklch(0% 0 0)) {
    filter: none;
  }

  ${variants({
    primary: {
      backgroundPosition: 'right top',
      backgroundSize: 'max(48rem, 110%) 100%',
      backgroundImage: `
        radial-gradient(50% 50% at 57%  3%, var(--color-0), transparent 50%),
        radial-gradient(50% 50% at 36% 76%, var(--color-0), transparent 50%),
        radial-gradient(100% 50% at 85% 56%, var(--color-1), transparent 50%),
        radial-gradient(150% 100% at 83% 25%, var(--color-1), transparent 50%),
        radial-gradient(50% 50% at 27% 23%, var(--color-1), transparent 50%),
        radial-gradient(50% 50% at 20% 35%, var(--color-1), transparent 50%)
      `,
    },
    secondary: {
      backgroundImage: `
        radial-gradient(max(80vw, 36rem) at 65% 44rem, var(--color-0), transparent 50%),
        radial-gradient(max(80vw, 36rem) at 30% 57rem, var(--color-0), transparent 50%),
        radial-gradient(max(60vw, 32rem) at 58% 24rem, var(--color-1), transparent 50%),
        radial-gradient(max(80vw, 36rem) at 50% 38rem, var(--color-1), transparent 50%),
        radial-gradient(max(80vw, 36rem) at 29% 50rem, var(--color-1), transparent 50%),
        radial-gradient(max(80vw, 36rem) at 60% 74rem, var(--color-1), transparent 50%)
      `,
    },
    tertiary: {
      backgroundImage: `
        radial-gradient(max(30vw, 36rem) at 50% 50%, var(--color-1), transparent 50%)
      `,
    },
    bottom: {
      backgroundImage: `
        radial-gradient(50% 50% at 50% 110%, var(--color-1), transparent 100%)
      `,
    },
    cta: {
      backgroundImage: `
        radial-gradient(min(100vw, 73rem) min(50vw, 64rem) at calc(50% + 18rem) 100%, var(--color-1), transparent 66.666%),
        radial-gradient(min(50vw, 54rem) min(50vw, 32rem) at calc(50% + 18rem) 100%, var(--color-2), transparent 60%)
      `,
      '@media (max-width: 64em)': {
        backgroundImage: `
          radial-gradient(max(100vw, 64rem) 54rem at 50% 100%, var(--color-1), transparent 66.666%),
          radial-gradient(max(75vw, 48rem) 32rem at 50% 100%, var(--color-2), transparent 50%)
        `,
      },
    },
    minimal: {
      backgroundImage: `
        radial-gradient(100vw 100vw at 85% 10%, var(--color-1), transparent 30%)
      `,
    },
    empty: {
      backgroundImage: 'none',
    },
  })}
`;

const Canvas = styled.canvas`
  inset: 0;
  opacity: 0;
  position: absolute;
  transition: opacity 1s linear;
  will-change: transform, opacity;

  &[data-mounted='true'] {
    opacity: 1;
  }
`;

export default function Starfield({ variant, ...props }) {
  const canvas = React.useRef();
  const starfield = React.useRef(new StarfieldCanvas());

  const container = useResizeObserver(
    React.useCallback((entry) => {
      if (canvas.current && !starfield.current.canvas) {
        canvas.current.dataset.mounted = 'true';
        starfield.current.canvas = canvas.current;
      }
      starfield.current.resize({
        height: entry.contentRect.height,
        width: entry.contentRect.width,
      });
    }, [])
  );

  useIntersectionObserver(
    React.useCallback((entry) => {
      if (canvas.current && !starfield.current.canvas) {
        canvas.current.dataset.mounted = 'true';
        starfield.current.canvas = canvas.current;
      }
      if (entry.isIntersecting) {
        starfield.current.start();
      } else {
        starfield.current.stop();
      }
    }, []),
    { ref: container }
  );

  React.useEffect(() => {
    return () => starfield.current.stop();
  }, []);

  return (
    <Container aria-hidden="true" {...props} ref={container}>
      <Clouds $variant={variant} />
      <Canvas ref={canvas} />
    </Container>
  );
}

export const StarfieldVariantProp = PropTypes.oneOf([
  'primary',
  'secondary',
  'tertiary',
  'bottom',
  'cta',
  'minimal',
  'empty',
]);

Starfield.propTypes = {
  variant: StarfieldVariantProp,
};

Starfield.defaultProps = {
  variant: 'secondary',
};

class StarfieldCanvas {
  /**
   * @param {HTMLCanvasElement} canvas
   */
  constructor(canvas) {
    this.size = { height: 0, width: 0 };
    this.stars = [];
    this.frameRequestId = 0;

    if (canvas) {
      this.canvas = canvas;
      this.resize({ height: canvas.clientHeight, width: canvas.clientWidth });
    }
  }

  createStars({ width, height }) {
    const area = width * height;
    const baseDensity = 0.0001;
    const starCount = Math.floor(area * baseDensity);

    if (this.stars.length < starCount) {
      for (let i = this.stars.length; i < starCount; i++) {
        this.stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          size: Math.random() * 2,
          opacity: Math.random(),
          state: Math.sign(0.5 - Math.random()),
        });
      }
    }

    return this.stars.slice(0, starCount);
  }

  draw() {
    if (!this.canvas) {
      return;
    }

    if (!this.offscreen) {
      this.offscreen = document.createElement('canvas');
    }

    const ctx = this.offscreen.getContext('2d');

    ctx.clearRect(0, 0, this.offscreen.width, this.offscreen.height);

    this.stars.forEach((star) => {
      const fac = 0.00075;
      star.opacity = Math.max(0, Math.min(1, star.state * fac + star.opacity));
      const { x, y, state, size, opacity } = star;
      if (opacity >= 1 || opacity <= 0) {
        star.state = -1 * state;
      }
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, 2 * Math.PI);
      ctx.fill();
    });

    this.canvas
      .getContext('2d')
      .clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvas.getContext('2d').drawImage(this.offscreen, 0, 0);

    return true;
  }

  resize({ height, width }) {
    if (!this.canvas) return;

    const pixelRatio = window.devicePixelRatio || 1;
    const adjustedWidth = width * pixelRatio;
    const adjustedHeight = height * pixelRatio;

    // Store new size
    this.size = { height: adjustedHeight, width: adjustedWidth };

    // Set canvas and offscreen dimensions
    this.canvas.width = adjustedWidth;
    this.canvas.height = adjustedHeight;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    if (!this.offscreen) {
      this.offscreen = document.createElement('canvas');
    }
    this.offscreen.width = adjustedWidth;
    this.offscreen.height = adjustedHeight;

    // Create a new batch of stars using the pixel-ratio adjusted dimensions
    this.stars = this.createStars({
      width: adjustedWidth,
      height: adjustedHeight,
    });

    // Ensure immediate draw on resize
    this.draw();

    // Avoid excessive resize calculations with debounce
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.draw();
    }, 100);
  }

  start() {
    if (document.hidden) return;

    this.stop();
    let lastFrameTime = 0;

    const update = (timestamp) => {
      if (document.hidden) return;
      if (!this.shouldRender) return; // Skip unnecessary renders

      if (timestamp - lastFrameTime < 1000 / 30) {
        this.frameRequestId = requestAnimationFrame(update);
        return;
      }

      lastFrameTime = timestamp;
      this.draw();
      this.frameRequestId = requestAnimationFrame(update);
    };

    this.shouldRender = true;
    update();
  }

  stop() {
    this.shouldRender = false;
    cancelAnimationFrame(this.frameRequestId);
  }
}
